import { uuid } from "@/lib/uuid";
import { businessDateFor } from "./businessDate";
import { redactUrl, redactValue } from "./redact";
import { appendEntries } from "./store";

/**
 * The single entry point every capture source writes through.
 *
 * Entries are buffered in memory and written to IndexedDB in small batches (every
 * second, or at 50), so a burst of taps on a slow phone costs one write instead of
 * fifty. The buffer is flushed when the page is hidden or closed.
 *
 * Logging is ON by default (owner, 2026-09-17). Turning it off records one final
 * entry saying so — otherwise the gap in a day's log would read as "nothing happened".
 */
const ENABLED_KEY = "vibez-logging-enabled";
const DEVICE_KEY = "vibez-device-id";
const FLUSH_DELAY_MS = 1000;
const FLUSH_AT = 50;
// Held in memory while storage is failing, so a brief IndexedDB hiccup loses nothing.
// Bounded: a device with broken storage must not grow memory until the tab dies.
const MAX_UNSTORED = 1000;

let buffer = [];
let flushTimer = null;
let flushing = null;
let actor = null;
const listeners = new Set();
export const loggerState = { storeError: null, unstoredDropped: 0 };

function notify() {
  listeners.forEach(listener => {
    try {
      listener();
    } catch {
      /* a broken listener must not stop logging */
    }
  });
}

export function subscribeLogger(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isLoggingEnabled() {
  try {
    return localStorage.getItem(ENABLED_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setLoggingEnabled(enabled) {
  if (!enabled) logEvent("logging_disabled", { by: actor?.employee_id || null }, { force: true });
  try {
    localStorage.setItem(ENABLED_KEY, enabled ? "on" : "off");
  } catch {
    /* private mode: the default (on) still applies next load */
  }
  if (enabled) logEvent("logging_enabled", { by: actor?.employee_id || null });
  notify();
}

/** Stable per browser, unlike the per-tab terminal id in sessionStorage. */
export function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch {
    return "unknown-device";
  }
}

/** Who is using the device now. Stamped into every entry as `actor`. */
export function setLogActor(user) {
  const next = user ? { employee_id: user.employee_id || user.employeeId || null, role: user.role || null } : null;
  const changed = JSON.stringify(next) !== JSON.stringify(actor);
  actor = next;
  if (changed) logEvent(next ? "session_user" : "session_cleared", next || {});
}

export function currentRoute() {
  try {
    return redactUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`).slice(0, 1000);
  } catch {
    return null;
  }
}

export function newRequestId() {
  return uuid().replace(/-/g, "");
}

/**
 * Record one thing that happened. Never throws.
 *
 * `force` writes even while logging is switched off — used only for the entry that
 * records logging being switched off.
 */
export function logEvent(kind, detail = {}, { requestId = null, force = false } = {}) {
  try {
    if (!force && !isLoggingEnabled()) return;
    const now = new Date();
    buffer.push({
      kind: String(kind).slice(0, 40),
      detail: { ...redactValue(detail), ...(actor ? { actor } : {}) },
      route: currentRoute(),
      request_id: requestId ? String(requestId).slice(0, 64) : null,
      occurred_at: now.toISOString(),
      business_date: businessDateFor(now),
    });
    if (buffer.length >= FLUSH_AT) {
      flushLogs();
    } else if (!flushTimer) {
      flushTimer = setTimeout(flushLogs, FLUSH_DELAY_MS);
    }
  } catch {
    /* logging never breaks the screen it is watching */
  }
}

/** Write the buffer to IndexedDB now. Resolves when this batch has been stored or kept. */
export function flushLogs() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!buffer.length) return flushing || Promise.resolve();
  const batch = buffer;
  buffer = [];
  const previous = flushing || Promise.resolve();
  const run = previous
    .catch(() => {})
    .then(() => appendEntries(batch))
    .then(() => {
      if (loggerState.storeError) {
        loggerState.storeError = null;
        notify();
      }
    })
    .catch(error => {
      // Keep what could not be stored and say so; retry on the next flush.
      const kept = batch.concat(buffer);
      const overflow = Math.max(kept.length - MAX_UNSTORED, 0);
      buffer = kept.slice(overflow);
      loggerState.unstoredDropped += overflow;
      loggerState.storeError = String(error?.message || error);
      // Retry on its own: a quiet screen produces no new events to trigger a flush.
      if (!flushTimer) flushTimer = setTimeout(flushLogs, 5000);
      notify();
    })
    .finally(() => {
      if (flushing === run) flushing = null;
    });
  flushing = run;
  return run;
}

/** For tests: forget in-memory state. */
export function __resetLoggerForTests() {
  buffer = [];
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  flushing = null;
  actor = null;
  loggerState.storeError = null;
  loggerState.unstoredDropped = 0;
  listeners.clear();
}
